// @refresh reload

import * as Sentry from "@sentry/solid"
import { createEffect, createSignal, onCleanup, Show } from "solid-js"
import { render } from "solid-js/web"
import { AppBaseProviders, AppInterface } from "@/app"
import { AppLoading } from "@/components/app-loading"
import { AuthProvider, useAuth } from "@/context/auth"
import {
  appBrokerAuthFetch,
  getControlPlaneAccessTokenForRoute,
  getForwardedOpenAnalystProviderToken,
  getOpenAnalystApiAccessToken,
} from "@/context/auth/broker"
import { createWebPlatformFetch } from "@/context/auth/platform-fetch"
import { type Platform, PlatformProvider } from "@/context/platform"
import { dict as en } from "@/i18n/en"
import { dict as zh } from "@/i18n/zh"
import {
  createHostedOpenAnalystRuntimeServerUrl,
  createHostedRuntimeSessionRefreshFetch,
  isHostedOpenAnalystAppHost,
} from "@/services/openanalyst-hosted-runtime"
import { createHostedStarterProject } from "@/services/hosted-projects"
import { createOpenAnalystRuntimeSession, type OpenAnalystRuntimeState } from "@/services/openanalyst-runtime-session"
import { createOpenAnalystSandboxClient } from "@/services/openanalyst-sandbox"
import { createSmartworkControlPlaneProxyFetch, createWebSmartworkRuntimeBootstrap } from "@/services/smartwork-web-runtime"
import { handleNotificationClick } from "@/utils/notification-click"
import { systemNotificationOptions } from "@/utils/notification-branding"
import "./plugins/register-inline-plugin-view"
import "./plugins/connect-integration-tool"
import "./plugins/open-app-tool"
import pkg from "../package.json"
import { ServerConnection } from "./context/server"

const DEFAULT_SERVER_URL_KEY = "openanalyst.settings.dat:defaultServerUrl"

const getLocale = () => {
  if (typeof navigator !== "object") return "en" as const
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of languages) {
    if (!language) continue
    if (language.toLowerCase().startsWith("zh")) return "zh" as const
  }
  return "en" as const
}

const getRootNotFoundError = () => {
  const key = "error.dev.rootNotFound" as const
  const locale = getLocale()
  return locale === "zh" ? (zh[key] ?? en[key]) : en[key]
}

const getStorage = (key: string) => {
  if (typeof localStorage === "undefined") return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const setStorage = (key: string, value: string | null) => {
  if (typeof localStorage === "undefined") return
  try {
    if (value !== null) {
      localStorage.setItem(key, value)
      return
    }
    localStorage.removeItem(key)
  } catch {
    return
  }
}

const readDefaultServerUrl = () => getStorage(DEFAULT_SERVER_URL_KEY)
const writeDefaultServerUrl = (url: string | null) => setStorage(DEFAULT_SERVER_URL_KEY, url)

const notify: Platform["notify"] = async (title, description, href) => {
  if (!("Notification" in window)) return

  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission().catch(() => "denied")
      : Notification.permission

  if (permission !== "granted") return

  const inView = document.visibilityState === "visible" && document.hasFocus()
  if (inView) return

  const notification = new Notification(title, systemNotificationOptions(description))

  notification.onclick = () => {
    handleNotificationClick(href)
    notification.close()
  }
}

const openLink: Platform["openLink"] = (url) => {
  window.open(url, "_blank")
}

const back: Platform["back"] = () => {
  window.history.back()
}

const forward: Platform["forward"] = () => {
  window.history.forward()
}

const restart: Platform["restart"] = async () => {
  window.location.reload()
}

const root = document.getElementById("root")
if (!(root instanceof HTMLElement) && import.meta.env.DEV) {
  throw new Error(getRootNotFoundError())
}

const getCurrentUrl = () => {
  if (location.hostname.includes("openanalyst.com")) return "http://localhost:4096"
  if (import.meta.env.DEV)
    return `http://${import.meta.env.VITE_OPENANALYST_SERVER_HOST ?? "127.0.0.1"}:${import.meta.env.VITE_OPENANALYST_SERVER_PORT ?? "4096"}`
  return location.origin
}

const getDefaultUrl = () => {
  const lsDefault = readDefaultServerUrl()
  if (lsDefault) return lsDefault
  return getCurrentUrl()
}

let activeWebRuntimeServerUrl = ""
let activeWebRuntimeRequiresBridge = false
let activeWebRuntimeRefreshServerUrl: (() => Promise<string | null>) | null = null
const smartworkRuntimeServerUrl = () => {
  if (!activeWebRuntimeRequiresBridge) return getDefaultUrl()
  return activeWebRuntimeServerUrl
}
const smartworkWebFetch = createSmartworkControlPlaneProxyFetch({
  getServerUrl: smartworkRuntimeServerUrl,
  fetch: globalThis.fetch.bind(globalThis),
  requireServerUrl: () => activeWebRuntimeRequiresBridge,
  refreshServerUrl: () => activeWebRuntimeRefreshServerUrl?.() ?? null,
})

function useHostedOpenAnalystRuntime() {
  if (import.meta.env.VITE_OPENANALYST_HOSTED_RUNTIME === "1") return true
  if (import.meta.env.VITE_OPENANALYST_HOSTED_RUNTIME === "0") return false
  return !import.meta.env.DEV && isHostedOpenAnalystAppHost(location.hostname)
}

const authenticatedWebPlatformFetch = createWebPlatformFetch({
  fetchImpl: globalThis.fetch.bind(globalThis),
  getAccessToken: getControlPlaneAccessTokenForRoute,
  getForwardedAccessToken: getForwardedOpenAnalystProviderToken,
  refreshAccessToken: appBrokerAuthFetch.refreshControlPlaneAccessTokenForRoute,
  env: import.meta.env,
}) as unknown as typeof fetch

const webPlatformFetch = createHostedRuntimeSessionRefreshFetch({
  fetch: authenticatedWebPlatformFetch,
  getServerUrl: () => activeWebRuntimeServerUrl,
  refreshServerUrl: () => activeWebRuntimeRefreshServerUrl?.() ?? null,
  requireServerUrl: () => activeWebRuntimeRequiresBridge,
})

const platform: Platform = {
  platform: "web",
  version: pkg.version,
  openLink,
  back,
  forward,
  restart,
  notify,
  fetch: webPlatformFetch,
  smartworkControlPlaneFetch: smartworkWebFetch,
  smartworkRuntimeBootstrap: createWebSmartworkRuntimeBootstrap({
    getServerUrl: smartworkRuntimeServerUrl,
    fetch: webPlatformFetch,
    controlPlaneFetch: smartworkWebFetch,
    refreshServerUrl: () => activeWebRuntimeRefreshServerUrl?.() ?? null,
    requireServerUrl: () => activeWebRuntimeRequiresBridge,
    getControlPlaneToken: getControlPlaneAccessTokenForRoute,
    getOpenAnalystToken: getOpenAnalystApiAccessToken,
  }),
  createStarterProject: async (input) => {
    const serverUrl = smartworkRuntimeServerUrl()
    if (!serverUrl) throw new Error("Hosted OpenAnalyst runtime is still starting.")
    return createHostedStarterProject({
      serverUrl,
      templateId: input.templateId,
      ...(input.binding ? { binding: input.binding } : {}),
      fetch: smartworkWebFetch,
      validateRuntimeContract: true,
    })
  },
  getDefaultServer: async () => {
    const stored = readDefaultServerUrl()
    return stored ? ServerConnection.Key.make(stored) : null
  },
  setDefaultServer: writeDefaultServerUrl,
  diagnosticsLog: async () => undefined,
  diagnosticsStatus: async () => ({
    enabled: false,
    logDir: "",
    currentFile: null,
    viewerUrl: null,
    retainedDays: 0,
    retainedBytes: 0,
  }),
  diagnosticsOpenViewer: async () => ({
    enabled: false,
    logDir: "",
    currentFile: null,
    viewerUrl: null,
    retainedDays: 0,
    retainedBytes: 0,
  }),
  diagnosticsOpenDirectory: async () => undefined,
  diagnosticsTail: async () => ({ entries: [], nextCursor: 0, logPath: null }),
}

function HostedRuntimeLoading(props: { state: OpenAnalystRuntimeState; message?: string }) {
  const message = () => {
    if (props.message) return props.message
    if (props.state.state === "provisioning" && props.state.progressMessage) return props.state.progressMessage
    if (props.state.state === "error") return props.state.message
    return "Preparing your hosted OpenAnalyst workspace..."
  }
  return <AppLoading message={message()} />
}

function OpenAnalystWebApp() {
  const auth = useAuth()
  const hostedRuntime = useHostedOpenAnalystRuntime()
  const [runtimeServerUrl, setRuntimeServerUrl] = createSignal<string | null>(hostedRuntime ? null : getDefaultUrl())
  const [runtimeState, setRuntimeState] = createSignal<OpenAnalystRuntimeState>({ state: "idle" })
  const [runtimeError, setRuntimeError] = createSignal<string | null>(null)

  let session: ReturnType<typeof createOpenAnalystRuntimeSession> | undefined
  let authKey = ""
  let readyVersion = 0
  let runtimeServerUrlResolution: Promise<string | null> | null = null

  const disposeRuntimeSession = () => {
    session?.dispose()
    session = undefined
    authKey = ""
    readyVersion += 1
    runtimeServerUrlResolution = null
  }

  const resolveReadyRuntimeServerUrl = (state: Extract<OpenAnalystRuntimeState, { state: "ready" }>) => {
    const version = ++readyVersion
    const resolution = createHostedOpenAnalystRuntimeServerUrl({
      connectUrl: state.connectUrl,
      publicOrigin: location.origin,
      fetch: globalThis.fetch.bind(globalThis),
      validate: true,
    })
      .then((url) => {
        if (version !== readyVersion) return null
        setRuntimeError(null)
        setRuntimeServerUrl(url)
        return url
      })
      .catch((error) => {
        if (version !== readyVersion) return null
        setRuntimeServerUrl(null)
        setRuntimeError(error instanceof Error ? error.message : "Hosted runtime session failed")
        return null
      })
    runtimeServerUrlResolution = resolution
    void resolution.finally(() => {
      if (runtimeServerUrlResolution === resolution) runtimeServerUrlResolution = null
    })
    return resolution
  }

  const resolveRuntimeServerUrl = (state: OpenAnalystRuntimeState) => {
    setRuntimeState(state)
    if (state.state === "error") {
      setRuntimeError(state.message)
      return
    }
    if (state.state !== "ready") return
    void resolveReadyRuntimeServerUrl(state)
  }

  const refreshRuntimeServerUrl = async () => {
    if (!session) return null
    const state = await session.reconnect()
    if (state.state !== "ready") return null
    return runtimeServerUrlResolution ?? resolveReadyRuntimeServerUrl(state)
  }

  createEffect(() => {
    if (!hostedRuntime) {
      disposeRuntimeSession()
      setRuntimeServerUrl(getDefaultUrl())
      return
    }

    const state = auth.state()
    if (!state.initialized || !state.authenticated) {
      disposeRuntimeSession()
      setRuntimeError(null)
      setRuntimeState({ state: "idle" })
      setRuntimeServerUrl(null)
      return
    }

    const nextAuthKey = `${state.session?.userId ?? ""}:${state.session?.workspaceId ?? ""}:${state.session?.projectId ?? ""}`
    if (session && authKey === nextAuthKey) return

    disposeRuntimeSession()
    authKey = nextAuthKey
    setRuntimeServerUrl(null)
    setRuntimeError(null)
    setRuntimeState({ state: "provisioning", progressMessage: "Connecting to hosted OpenAnalyst workspace" })

    session = createOpenAnalystRuntimeSession({
      client: createOpenAnalystSandboxClient({
        apiBaseUrl: location.origin,
        fetch: globalThis.fetch.bind(globalThis),
        source: "api",
        getAccessToken: getControlPlaneAccessTokenForRoute,
        refreshAccessToken: appBrokerAuthFetch.refreshControlPlaneAccessTokenForRoute,
      }),
      onState: resolveRuntimeServerUrl,
    })
    void session.start()
  })

  onCleanup(disposeRuntimeSession)

  const appServerUrl = () => {
    if (!hostedRuntime) return getDefaultUrl()
    if (!auth.state().authenticated) return getCurrentUrl()
    return runtimeServerUrl()
  }

  createEffect(() => {
    activeWebRuntimeRequiresBridge = hostedRuntime
    activeWebRuntimeServerUrl = hostedRuntime ? runtimeServerUrl() ?? "" : appServerUrl() ?? ""
    activeWebRuntimeRefreshServerUrl = hostedRuntime ? refreshRuntimeServerUrl : null
  })

  onCleanup(() => {
    activeWebRuntimeRequiresBridge = false
    activeWebRuntimeServerUrl = ""
    activeWebRuntimeRefreshServerUrl = null
  })

  return (
    <Show
      when={appServerUrl()}
      keyed
      fallback={<HostedRuntimeLoading state={runtimeState()} message={runtimeError() ?? undefined} />}
    >
      {(serverUrl) => {
        const server: ServerConnection.Http = {
          type: "http",
          http: { url: serverUrl },
          ...(hostedRuntime ? { displayName: "OpenAnalyst Cloud" } : {}),
        }
        return (
          <AppInterface
            defaultServer={ServerConnection.Key.make(serverUrl)}
            servers={[server]}
            disableHealthCheck
          />
        )
      }}
    </Show>
  )
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? `web@${pkg.version}`,
    initialScope: {
      tags: {
        platform: "web",
      },
    },
    integrations: (integrations) => {
      return integrations.filter(
        (i) =>
          i.name !== "Breadcrumbs" && !(import.meta.env.OPENANALYST_CHANNEL === "prod" && i.name === "GlobalHandlers"),
      )
    },
  })
}

if (root instanceof HTMLElement) {
  render(
    () => (
      <PlatformProvider value={platform}>
        <AuthProvider>
          <AppBaseProviders>
            <OpenAnalystWebApp />
          </AppBaseProviders>
        </AuthProvider>
      </PlatformProvider>
    ),
    root,
  )
}
